/* 지도 위에 떠 있는 로그인 / 계정 버튼.
 *
 * 이게 존재하는 이유는 UI 취향이 아니라 **팝업 차단** 때문이다. 페이지가 뜨자마자
 * 로그인 팝업을 열면 브라우저가 막고, 화면에는 "기록을 불러오지 못했습니다"만 남아서
 * 원인이 안 보인다. 클릭에서 시작된 팝업은 막히지 않는다 — 그래서 로그인은 반드시
 * 사람이 누르는 자리에서 시작해야 한다. (lib/firebase.js 의 signIn 주석 참조)
 *
 * 지도 위에 두는 것도 같은 맥락이다. 이 앱은 화면이 지도 하나뿐이라 툴바에 넣으면
 * 목록에 가려 안 보인다.
 */

import { useState } from "react";

import { LogOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { cn } from "../lib/util.js";
import { FLOAT_BTN } from "./Overlays.jsx";

const CIRCLE = `${FLOAT_BTN} size-10 rounded-full`;

/* 로그아웃 상태는 아이콘이 아니라 글자다. 아이콘만 두면 '내 계정'인지 '로그인'인지
   눌러보기 전에는 알 수 없고, 로그인은 안 해본 사람에게 가장 먼저 보여야 하는
   동작이다. 로그인한 뒤에는 얼굴·이니셜이 그 자리를 대신하므로 원형으로 돌아간다. */
const PILL = `${FLOAT_BTN} h-10 rounded-full px-4 text-[13.5px] font-bold text-primary`;

/** 표시 이름의 첫 글자. 이름이 없으면 이메일, 그것도 없으면 물음표. */
function initial(user) {
  const src = (user.displayName || user.email || "").trim();
  return src ? Array.from(src)[0].toUpperCase() : "?";
}

export function UserButton({ user, photo, onSignIn, onSignOut, busy }) {
  const [open, setOpen] = useState(false);
  /* localStorage 의 캐시는 코드보다 오래 산다. 예전 형식이 남아 이미지가 깨지면
     빈 동그라미가 되는데, 그건 고장으로 보인다. 이니셜로 돌아가는 편이 낫다. */
  const [broken, setBroken] = useState(false);
  const avatar = photo && !broken;

  /* 자리는 App 의 MapChrome 이 정한다. 여기서 좌표를 들고 있으면 옆에 놓이는
     버튼과 간격을 상수로 맞춰야 하고, 이 버튼이 알약↔원형으로 폭이 바뀔 때마다
     그 상수가 틀어진다. */
  if (!user) {
    return (
      <Button
        className={cn(PILL, "user-btn")}
        disabled={busy}
        id="btn-login"
        onClick={onSignIn}
        title="Microsoft 계정으로 로그인"
        variant="ghost"
      >
        로그인
      </Button>
    );
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-label="계정"
          className={cn(CIRCLE, "user-btn text-[15px] font-bold",
                        avatar && "overflow-hidden p-0")}
          id="btn-user"
          size="icon-lg"
          variant="ghost"
        >
          {avatar
            ? <img
                alt=""
                className="size-full object-cover"
                onError={() => setBroken(true)}
                src={photo}
              />
            : initial(user)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto min-w-48 p-3" sideOffset={8}>
        {/* 이름이 없을 수 있다 — Entra 토큰이 name 클레임을 안 주는 경우가 있어서,
            그때는 이메일만 뜬다. 둘 다 없으면 uid 라도 보여준다. 화면이 비는 것보다 낫다. */}
        <div className="mb-2.5 min-w-0">
          {user.displayName && (
            <div className="truncate text-[13.5px] font-bold">{user.displayName}</div>
          )}
          <div className="truncate text-[12px] text-muted-foreground">
            {user.email || user.uid}
          </div>
        </div>
        <Button
          className="w-full justify-start gap-2"
          id="btn-signout"
          onClick={() => { setOpen(false); onSignOut(); }}
          size="sm"
          variant="ghost"
        >
          <LogOut className="size-[14px] flex-none" />
          로그아웃
        </Button>
      </PopoverContent>
    </Popover>
  );
}
